import { PageContext, type BreadcrumbItem } from "@contexts/PageContext";
import type { ModalState, ViewMode } from "@typings/pages";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { config } from "@/config";

export function PageProvider({ children }: { children: React.ReactNode }) {
  const [activePage, setActivePage] = useState("Início");
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([
    { label: "Início", path: "/" },
  ]);
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [currentModal, setCurrentModal] = useState<ModalState>(null);
  const [dataModal, setDataModal] = useState<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();

  const [areaMap, setAreaMap] = useState<GeoJSON.FeatureCollection | null>(
    null,
  );

  const pathToPage = useMemo(() => {
    const map: Record<
      string,
      {
        activePage: string;
        breadcrumb: BreadcrumbItem[];
      }
    > = {};

    for (const page of config.pages) {
      if (page.path) {
        map[page.path] = {
          activePage: page.label,
          breadcrumb: [{ label: page.label, path: page.path }],
        };
      }
      if (page.subpages) {
        for (const subpage of page.subpages) {
          if (subpage.path) {
            map[subpage.path] = {
              activePage: subpage.label,
              breadcrumb: [
                { label: page.label, path: page.path },
                { label: subpage.label, path: subpage.path },
              ],
            };
          }
        }
      }
    }
    return map;
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;

    const pageInfo = pathToPage[currentPath];
    if (pageInfo) {
      setActivePage(pageInfo.activePage);
      setBreadcrumb(pageInfo.breadcrumb);
      return;
    }

    for (const page of config.pages) {
      if (page.subpages) {
        for (const subpage of page.subpages) {
          if (subpage.path && currentPath.startsWith(subpage.path + "/")) {
            const pathSegments = currentPath.split("/");
            const itemId = pathSegments[pathSegments.length - 1];

            setActivePage(`${subpage.label} - ${itemId}`);
            setBreadcrumb([
              { label: page.label, path: page.path },
              { label: subpage.label, path: subpage.path },
              { label: itemId, path: currentPath },
            ]);
            return;
          }
        }
      }

      if (page.path && currentPath.startsWith(page.path + "/")) {
        const pathSegments = currentPath.split("/");
        const itemId = pathSegments[pathSegments.length - 1];

        setActivePage(`${page.label} - ${itemId}`);
        setBreadcrumb([
          { label: page.label, path: page.path },
          { label: itemId, path: currentPath },
        ]);
        return;
      }
    }
  }, [location.pathname, pathToPage]);

  useEffect(() => {
    fetch("/data/areas.json")
      .then((r) => {
        if (!r.ok) throw new Error("Falha ao carregar areas.json");
        return r.json();
      })
      .then(setAreaMap)
      .catch(() => {
        setAreaMap(null);
      });
  }, []);

  const openModal = useCallback((modalName: string) => {
    setCurrentModal(modalName);
  }, []);

  const closeModal = useCallback(() => {
    setCurrentModal(null);
  }, []);

  const isModalOpen = useCallback(
    (modalName: string) => currentModal === modalName,
    [currentModal],
  );

  const value = useMemo(
    () => ({
      activePage,
      setActivePage,
      breadcrumb,
      setBreadcrumb,
      viewMode,
      setViewMode,
      openModal,
      closeModal,
      isModalOpen,
      currentModal,
      setDataModal,
      dataModal,
      isLoading,
      setIsLoading,
      areaMap,
      setAreaMap,
    }),
    [
      activePage,
      breadcrumb,
      viewMode,
      openModal,
      closeModal,
      isModalOpen,
      currentModal,
      dataModal,
      isLoading,
      setIsLoading,
      areaMap,
      setAreaMap,
    ],
  );

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
}
